/**
 * Check, anonymize and upload local files on several engines at once (ANON-04, UPL-01 to UPL-07).
 * Only anonymized bytes leave this module; originals are read, handed to an engine and released.
 */
import type { Analysis, Facts, Overrides, Result, Target } from '../anonymization/engine'
import type { EnginePool } from '../anonymization/pool'
import { api, isSessionEnd, sleep, type ApiError } from '../lib/api'
import { Slots, eachLimited } from '../lib/concurrency'
import { sha256 } from '../lib/sha256'
import type { FileState, UploadDetail, UploadFile } from '../lib/types'
import { withCorrections } from './labelMap'
import type { Sent } from './sent'
import { UnreadableError, type Source } from './zip'

/** `anonymized`: anonymized in memory, waiting for a free transfer. */
export type ItemStatus = 'waiting' | 'blocked' | 'processing' | 'anonymized' | 'transferring' | 'failed' | FileState

/** What the pipeline needs of the engines (`anonymization/pool.ts`). */
export type Engines = Pick<EnginePool, 'engines' | 'analyze' | 'process'>

export interface Item {
  key: string
  file: Source
  status: ItemStatus
  reason?: string
  analysis?: Analysis
  /** Values the uploader corrected or supplied (ANON-06). */
  overrides?: Overrides
  serverId?: string
}

/** Transfers at once; anonymized files beyond them wait in memory, at most one per engine. */
const TRANSFERS = 4
const RETRIES = 3
const POLL_MS = 2000
const POLL_FAILURES = 5
const FINAL: ReadonlySet<ItemStatus> = new Set(['accepted', 'rejected', 'duplicate', 'blocked', 'failed'])
const IN_HAND: ReadonlySet<ItemStatus> = new Set(['processing', 'anonymized', 'transferring'])

const isFinal = (status: ItemStatus) => FINAL.has(status)

const failed = (code: string): Result<never> => ({ ok: false, error: { code, field: null, message: '' } })

/** Batch state outside React, read by the UI with useSyncExternalStore. */
export class ItemStore {
  private items: readonly Item[] = []
  private readonly listeners = new Set<() => void>()

  readonly snapshot = () => this.items

  readonly subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => void this.listeners.delete(listener)
  }

  replace(files: readonly Source[]): void {
    this.items = files.map((file, index) => ({ key: `${index}:${file.name}`, file, status: 'waiting' }))
    this.emit()
  }

  update(key: string, change: Partial<Item>): void {
    this.updateMany(new Map([[key, change]]))
  }

  /** Applies many changes with one copy and one notification, so large batches stay responsive. */
  updateMany(changes: ReadonlyMap<string, Partial<Item>>): void {
    if (changes.size === 0) return
    this.items = this.items.map((item) => {
      const change = changes.get(item.key)
      return change ? { ...item, ...change } : item
    })
    this.emit()
  }

  /** Adds corrected or supplied values to the files (ANON-06); they need checking again. */
  correct(keys: ReadonlySet<string>, overrides: Overrides): void {
    const chosen = this.items.filter((item) => keys.has(item.key))
    this.updateMany(new Map(chosen.map((item) => [item.key, { overrides: withCorrections(item.overrides, overrides) }])))
  }

  /** Forgets the check of the files not sent yet, keeping their corrections: they need checking again. */
  reset(): void {
    this.items = this.items.map((item) => (item.serverId ? item : { ...item, status: 'waiting', reason: undefined, analysis: undefined }))
    this.emit()
  }

  /** Marks the files an error left half done as failed, so the batch can be checked again. */
  failInHand(reason: string): void {
    const inHand = this.items.filter((item) => IN_HAND.has(item.status))
    this.updateMany(new Map<string, Partial<Item>>(inHand.map((item) => [item.key, { status: 'failed', reason }])))
  }

  private emit(): void {
    for (const listener of this.listeners) listener()
  }
}

/** A file's bytes, or the code of why they cannot be read: moved away, or an archive entry this browser cannot unpack. */
async function read(file: Source): Promise<ArrayBuffer | string> {
  try {
    return await file.arrayBuffer()
  } catch (error) {
    return error instanceof UnreadableError ? error.code : 'unreadable'
  }
}

/** Not released with the upload: held, failed or refused by the platform. */
const UNRELEASED: ReadonlySet<ItemStatus> = new Set(['blocked', 'failed', 'rejected'])
const UNRESOLVED = 'unresolved_instance_reference'

/**
 * The files of the upload that `item` refers to and that are released with it, as a stitched image
 * refers to its exposures: its references resolve to their released images (References).
 */
export function targetsOf(item: Item, items: readonly Item[]): Item[] {
  const refers = new Set(item.analysis?.refers)
  const found = new Map<string, Item>()
  if (refers.size === 0) return []
  for (const other of items) {
    const instance = other.analysis?.instance
    if (instance && refers.has(instance) && other.key !== item.key && !UNRELEASED.has(other.status) && !found.has(instance)) found.set(instance, other)
  }
  return [...found.values()]
}

/**
 * The files whose references resolve differently once the files `keys` are left out or taken back: those
 * ready, or held only by their references, that refer to one of them (References). Files the uploader
 * left out stay out.
 */
export function referringTo(keys: ReadonlySet<string>, items: readonly Item[]): Set<string> {
  const instances = new Set(items.flatMap((item) => (keys.has(item.key) && item.analysis?.instance ? [item.analysis.instance] : [])))
  const open = (item: Item) => item.status === 'waiting' || (item.status === 'blocked' && item.reason?.split(', ').includes(UNRESOLVED))
  const referring = items.filter((item) => !keys.has(item.key) && open(item) && item.analysis?.refers?.some((link) => instances.has(link)))
  return new Set(referring.map((item) => item.key))
}

/** Checks one file with its corrections (ANON-05, ANON-06), its references resolving to the files of the upload it names. */
async function analyzeFile(engines: Engines, item: Item, items: readonly Item[]): Promise<Result<Analysis>> {
  const bytes = await read(item.file)
  const present = targetsOf(item, items).map((target) => target.analysis!.instance!)
  return typeof bytes === 'string' ? failed(bytes) : engines.analyze(bytes, item.overrides, present)
}

/** Anonymizes one file with its corrections, in memory, with the originals of the upload it refers to. */
export async function anonymizeFile(engines: Engines, item: Item, items: readonly Item[]): Promise<Result<{ data: Uint8Array; facts: Facts }>> {
  const bytes = await read(item.file)
  if (typeof bytes === 'string') return failed(bytes)
  const targets: Target<ArrayBuffer>[] = []
  for (const target of targetsOf(item, items)) {
    const source = await read(target.file)
    if (typeof source !== 'string') targets.push({ source, overrides: target.overrides })
  }
  return engines.process(bytes, item.overrides, targets)
}

/** Records a file's check: ready, or held with its blocking problems. */
function recordCheck(store: ItemStore, item: Item, result: Result<Analysis>): void {
  if (!result.ok) return store.update(item.key, { status: 'blocked', reason: result.error.code, analysis: undefined })
  const blockers = result.value.issues.filter((issue) => issue.level === 'block')
  store.update(item.key, {
    status: blockers.length ? 'blocked' : 'waiting',
    reason: blockers.map((issue) => issue.code).join(', ') || undefined,
    analysis: result.value,
  })
}

/**
 * Checks again each file held because it refers to images the upload releases too, once their checks
 * are known: its references then resolve (References). A file that becomes ready may resolve another
 * one's, so this repeats until nothing changes.
 */
async function linkReferences(engines: Engines, store: ItemStore, cancelled: () => boolean): Promise<void> {
  const tried = new Map<string, string>() // each file's targets when it was last checked
  for (;;) {
    const items = store.snapshot()
    const held = items.filter((item) => item.status === 'blocked' && item.reason?.split(', ').includes(UNRESOLVED))
    const again = held.filter((item) => {
      const targets = targetsOf(item, items).map((target) => target.key).join('\n')
      if (!targets || tried.get(item.key) === targets) return false
      tried.set(item.key, targets)
      return true
    })
    if (again.length === 0 || cancelled()) return
    await eachLimited(again, () => engines.engines, async (item) => recordCheck(store, item, await analyzeFile(engines, item, store.snapshot())), cancelled)
  }
}

/** A held-back file's original as the browser can show it, in memory: nothing is anonymized or sent. */
export async function viewableFile(engines: Pick<EnginePool, 'viewable'>, item: Item): Promise<Result<Uint8Array>> {
  const bytes = await read(item.file)
  return typeof bytes === 'string' ? failed(bytes) : engines.viewable(bytes)
}

/** Reached the platform: stored, or already there. */
const SENT: ReadonlySet<ItemStatus> = new Set(['accepted', 'duplicate'])

/**
 * Reads every file, or only the given ones, on all engines at once, and records blocking problems
 * (ANON-05). Files this browser sent before (`sentBefore`) are not read again (UPL-04).
 */
export async function checkFiles(
  engines: Engines,
  store: ItemStore,
  cancelled: () => boolean,
  { only, sentBefore }: { only?: ReadonlySet<string>; sentBefore?: (file: Source) => boolean } = {},
) {
  const chosen = store.snapshot().filter((candidate) => !only || only.has(candidate.key))
  await eachLimited(
    chosen,
    () => engines.engines,
    async (item) => {
      if (sentBefore?.(item.file)) return store.update(item.key, { status: 'duplicate', reason: 'sent_before' })
      store.update(item.key, { status: 'processing' })
      recordCheck(store, item, await analyzeFile(engines, item, store.snapshot()))
    },
    cancelled,
  )
  await linkReferences(engines, store, cancelled)
}

export interface UploadHooks {
  /** Files that reached the platform, with their images (UPL-04). */
  onSent?: (sent: Sent[]) => void
  /** Bytes transferred, for the transfer rate. */
  onBytes?: (bytes: number) => void
}

/**
 * Anonymizes the checked files on all engines at once and transfers them, a few at a time. An
 * engine takes the next file only once its last one is being transferred, so memory holds at most
 * one anonymized file per engine besides the transfers.
 */
export async function uploadFiles(
  engines: Engines,
  uploadId: string,
  store: ItemStore,
  cancelled: () => boolean,
  { onSent = () => {}, onBytes = () => {} }: UploadHooks = {},
): Promise<void> {
  const transfers = new Slots(TRANSFERS)
  let sessionEnd: ApiError | undefined // nothing more can be sent; anonymizing further is wasted
  const waiting = store.snapshot().filter((candidate) => candidate.status === 'waiting' && candidate.analysis)
  await eachLimited(
    waiting,
    () => engines.engines,
    async (item) => {
      store.update(item.key, { status: 'processing' })
      const result = await anonymizeFile(engines, item, store.snapshot())
      // A file another one refers to failed meanwhile: the uploader can still omit the reference.
      const held = !result.ok && result.error.code === UNRESOLVED
      if (!result.ok) return store.update(item.key, { status: held ? 'blocked' : 'failed', reason: result.error.code })
      const digest = await sha256(result.value.data)
      store.update(item.key, { status: 'anonymized' })
      await transfers.take()
      try {
        if (sessionEnd) return store.update(item.key, { status: 'waiting' }) // sent after the next login
        store.update(item.key, { status: 'transferring' })
        const { data, facts } = result.value
        const file = await transfer(uploadId, data, facts, digest)
        if (file.state === 'uploaded') onBytes(data.byteLength)
        store.update(item.key, { status: file.state, reason: file.reason ?? undefined, serverId: file.id })
        if (SENT.has(file.state) && file.image_id) onSent([[item.file, file.image_id]]) // known already, nothing transferred
      } catch (error) {
        if (isSessionEnd(error)) sessionEnd = error
        store.update(item.key, { status: 'failed', reason: error instanceof Error ? error.message : 'transfer_failed' })
      } finally {
        transfers.give()
      }
    },
    () => cancelled() || sessionEnd !== undefined,
  )
  if (sessionEnd) throw sessionEnd
}

async function transfer(uploadId: string, data: Uint8Array, facts: Facts, digest: string): Promise<UploadFile> {
  // Starting the upload confirmed the files it sends (UPL-02); the confirmation is bound to these bytes (ANON-14).
  await api('/uploads/reviews', { json: { sha256s: [digest] } })
  const file = await api<UploadFile>(`/uploads/${uploadId}/files`, {
    json: {
      name: facts.filename,
      size: data.byteLength,
      dedup_key: facts.dedupKey,
      content_digest: facts.contentDigest,
      sha256: digest,
      annotated: facts.annotated,
    },
  })
  if (!file.upload_url) return file // known image, nothing to transfer (UPL-07)
  // A path for hosted storage (served under this origin), or the site's own bucket (STO-07).
  const url = file.upload_url
  await withRetries(async () => {
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/dicom' },
      body: data as Uint8Array<ArrayBuffer>,
    })
    if (!response.ok) throw new Error(`transfer_failed_${response.status}`)
  })
  await api(`/uploads/${uploadId}/files/${file.id}/uploaded`, { method: 'POST' })
  return { ...file, state: 'uploaded' }
}

async function withRetries(action: () => Promise<void>): Promise<void> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await action()
    } catch (error) {
      if (attempt >= RETRIES) throw error
      await sleep(1000 * 2 ** attempt)
    }
  }
}

/**
 * Follows server-side validation until every transferred file has a final state (UPL-06). Each
 * poll asks only for the results after the last one seen, so it stays small for thousands of
 * files. A failed poll is tried again with backoff; a session that ended, or five failures in a
 * row, stop it.
 */
export async function followValidation(uploadId: string, store: ItemStore, cancelled: () => boolean, onSent: (sent: Sent[]) => void = () => {}) {
  const open = () => store.snapshot().filter((item) => item.serverId && !isFinal(item.status))
  let failures = 0
  let results = 0
  for (let waiting = open(); waiting.length && !cancelled(); waiting = open()) {
    await sleep(POLL_MS * 2 ** failures)
    let detail: UploadDetail
    try {
      detail = await api<UploadDetail>(`/uploads/${uploadId}?results_after=${results}`)
      results = detail.results
      failures = 0
    } catch (error) {
      if (isSessionEnd(error) || ++failures >= POLL_FAILURES) throw error
      continue
    }
    const states = new Map(detail.files.map((file) => [file.id, file]))
    const changes = new Map<string, Partial<Item>>()
    const sent: Sent[] = []
    for (const item of waiting) {
      const file = states.get(item.serverId!)
      const reason = file?.reason ?? undefined
      if (!file || (file.state === item.status && reason === item.reason)) continue
      changes.set(item.key, { status: file.state, reason })
      if (SENT.has(file.state) && file.image_id) sent.push([item.file, file.image_id])
    }
    store.updateMany(changes)
    onSent(sent)
  }
}
